import { useFieldArray } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import { GrowthField } from '../fields/GrowthField';
import { TimePointField } from '../fields/TimePointField';
import type { SectionProps } from '../sectionProps';

/**
 * INCOME section — a `useFieldArray` over `incomes`. Each row edits an `IncomeStream`: `name`,
 * `amount` (annual at start), the shared `GrowthField` (fixed | CPI-indexed), an `assessable` toggle
 * (Thai PIT), and `start` / optional `end` via the shared `TimePointField` (absolute year OR
 * event-relative). Event-relative references validate against the plan's events via `PlanSchema`'s
 * superRefine and are surfaced on the section-level `events` error.
 */
export function IncomeSection({ form }: SectionProps): React.ReactElement {
  const { register, control, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'incomes' });
  const errors = formState.errors.incomes;
  const eventsError = formState.errors.events;

  return (
    <section>
      <h2>Income</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        Inflows like salary or a pension. End a stream on a calendar year or relative to an event
        (e.g. "until retirement").
      </p>

      {typeof eventsError?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {eventsError.message}
        </p>
      )}

      {fields.map((f, i) => {
        const rowErrors = errors?.[i];
        return (
          <div key={f.id} className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <FieldRow>
              <div style={{ flex: 2, minWidth: 180 }}>
                <Field label="Name" error={rowErrors?.name} htmlFor={`incomes.${i}.name`}>
                  <input
                    id={`incomes.${i}.name`}
                    className="input"
                    {...register(`incomes.${i}.name`)}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <Field
                  label="Amount ฿ / yr"
                  error={rowErrors?.amount}
                  htmlFor={`incomes.${i}.amount`}
                >
                  <input
                    id={`incomes.${i}.amount`}
                    className="input input--num"
                    type="number"
                    {...register(`incomes.${i}.amount`, { valueAsNumber: true })}
                  />
                </Field>
              </div>
            </FieldRow>

            <GrowthField
              form={form}
              name={`incomes.${i}.growthMode`}
              error={rowErrors?.growthMode}
            />

            <FieldRow>
              <div style={{ flex: 1, minWidth: 220 }}>
                <TimePointField
                  form={form}
                  name={`incomes.${i}.start`}
                  label="Start"
                  error={rowErrors?.start}
                />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <TimePointField
                  form={form}
                  name={`incomes.${i}.end`}
                  label="End"
                  optional
                  error={rowErrors?.end}
                />
              </div>
            </FieldRow>

            <label className="checkrow">
              <input type="checkbox" {...register(`incomes.${i}.assessable`)} />
              <span>Assessable for Thai personal income tax</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(i)}
                aria-label={`Remove income ${i + 1}`}
              >
                Remove income
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `income${fields.length + 1}`,
            name: '',
            amount: 0,
            growth: 0,
            growthMode: { kind: 'fixed', rate: 0 },
            start: { kind: 'year', year: form.getValues('startYear') },
            assessable: true,
          })
        }
      >
        + Add income
      </button>
    </section>
  );
}
